import { FC } from 'react'

import { Widget } from '@factorialco/f0-react/dist/experimental'
import {
  BankingBankAccountOverviewFragment,
  Exact,
  FinanceOverviewBankAccountsDocument,
  FinanceOverviewBankAccountsQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { EmptyState } from 'modules/finance/components/Widgets/common/EmptyState'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

import { BankAccountItem } from './_BankAccountItem'

export interface BankAccountsPreparedData {
  amountFormatter: (value: number, currency: string) => string
  bankAccounts: BankingBankAccountOverviewFragment[]
  summaries?: { label: string; value: string }[]
}

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency: string) => string
}

const prepareData = (
  data: FinanceOverviewBankAccountsQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const bankAccounts: BankingBankAccountOverviewFragment[] =
    data.banking.bankAccountsConnection.nodes
  const currencyData = data?.banking?.bankAccountsConnection?.aggregate?.terms?.currency
  const currencyValues = currencyData?.values || []

  const balanceByCurrency = currencyValues.reduce<Record<string, number>>(
    (obj, currency, index) => {
      obj[currency] = currencyData?.sum?.accountBalanceCents?.[index] ?? 0
      return obj
    },
    {}
  )

  const summaries = currencyValues.length
    ? Object.entries(balanceByCurrency).map(([key, value]) => {
        return {
          label: `${i18n.t('finance.overview.banks.all_accounts')} (${key.toUpperCase()})`,
          value: prepareDataArgs.amountFormatter(Number(value), key),
        }
      })
    : undefined

  return {
    amountFormatter: prepareDataArgs.amountFormatter,
    bankAccounts: bankAccounts,
    summaries: summaries,
  }
}

const BankAccountsListWidget: FC<{
  data: BankAccountsPreparedData
}> = ({ data }) => {
  const { navigation } = useNavigation()
  const { bankAccounts, summaries } = data

  return (
    <Widget
      header={{
        link: {
          title: i18n.t('finance.overview.banks.link_title'),
          url: navigation.finance.banking.accounts.fullPath(),
        },
        title: i18n.t('finance.overview.banks.title'),
      }}
      summaries={summaries}
    >
      {bankAccounts.length > 0 ? (
        bankAccounts.map((bankAccount) => (
          <BankAccountItem
            key={'bankaccount_' + bankAccount.id}
            bankAccount={bankAccount}
            amountFormatter={data.amountFormatter}
          />
        ))
      ) : (
        <EmptyState icon="alert" textLine1={i18n.t('finance.overview.banks.empty_state_line_1')} />
      )}
    </Widget>
  )
}

export const buildBankAccountsListWidget = ({
  prepareDataArgs,
}: {
  prepareDataArgs: PreparedDataArgsType
}) =>
  buildWidget<
    FinanceOverviewBankAccountsQuery,
    BankAccountsPreparedData,
    Exact<{ endDate: string; startDate: string }>
  >({
    id: 'bankAccountsListWidget',
    component: BankAccountsListWidget,
    document: FinanceOverviewBankAccountsDocument,
    prepareData: (data: FinanceOverviewBankAccountsQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
    },
  })
